/**
 * <arcero-bundle-product> — reads the currently resolved variant from
 * each independent native <variant-picker> rendered for a bundle's
 * component products (see sections/arcero-bundle-product.liquid), and
 * adds them all to the cart in one POST to /cart/add.js. The bundle
 * "wrapper" product's own variant is never added — only the real
 * component variants. Discount is a normal Shopify automatic discount
 * set up in Admin, not built here.
 */
import { StandardEvents, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class ArceroBundleProduct extends HTMLElement {
  /** @type {Map<Element, Promise<unknown>>} */
  #pending = new Map();

  connectedCallback() {
    this.addButton = this.querySelector('[data-arcero-bundle-add]');
    this.errorEl = this.querySelector('[data-arcero-bundle-error]');

    // Each nested variant-picker dispatches a bubbling ProductSelectEvent
    // (StandardEvents.productSelect) whenever a selection resolves. Track
    // its promise per-picker so a click right after a fast option change
    // can await the in-flight resolution instead of reading a stale
    // variant id from the picker's own <script type="application/json">.
    this.addEventListener(StandardEvents.productSelect, (event) => {
      if (!(event.target instanceof Element)) return;
      const picker = event.target;
      const promise = event.promise;
      this.#pending.set(picker, promise);
      promise.finally(() => {
        if (this.#pending.get(picker) === promise) this.#pending.delete(picker);
      });
    });

    this.addButton?.addEventListener('click', () => this.addBundleToCart());
  }

  async addBundleToCart() {
    if (!this.addButton) return;

    this.#hideError();
    this.addButton.disabled = true;

    try {
      // Wait out any variant resolution still in flight so we never read
      // a picker's script tag before it's been updated for the buyer's
      // latest click.
      await Promise.allSettled(this.#pending.values());

      const pickers = Array.from(this.querySelectorAll('variant-picker'));
      if (pickers.length === 0) return;

      const items = [];

      for (const picker of pickers) {
        const script = picker.querySelector('script[type="application/json"]');
        let variant = null;
        try {
          variant = script ? JSON.parse(script.textContent ?? '') : null;
        } catch (error) {
          variant = null;
        }

        if (!variant || !variant.id || variant.available === false) {
          this.#showError();
          return;
        }

        items.push({ id: variant.id, quantity: 1 });
      }

      const deferred = CartLinesUpdateEvent.createPromise();

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
          promise: deferred.promise,
        })
      );

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        deferred.reject(new Error(data.message || 'Add to cart failed'));
        this.#showError();
        return;
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`);
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'arcero-bundle-product', didError: false },
      });
    } catch (error) {
      console.error(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#showError();
    } finally {
      this.addButton.disabled = false;
    }
  }

  #showError() {
    this.errorEl?.removeAttribute('hidden');
  }

  #hideError() {
    this.errorEl?.setAttribute('hidden', '');
  }
}

if (!customElements.get('arcero-bundle-product')) {
  customElements.define('arcero-bundle-product', ArceroBundleProduct);
}
